export const initialState = {
    documents: [],
    activeDocumentId: null,
    selectedElementId: null,
};
export const consentReducer = (state, action) => {
    switch (action.type) {
        case 'LOAD_DOCUMENTS': {
            const existingById = new Map(state.documents.map((doc) => [doc.id, doc]));
            const incomingIds = new Set(action.payload.map((doc) => doc.id));
            const merged = action.payload.map((doc) => {
                const existing = existingById.get(doc.id);
                if (!existing) {
                    return doc;
                }
                // `false` means this payload read the role list and it is healthy, including a repaired
                // empty list. A row that omitted roles leaves the flag unset, so it must keep the hydrated
                // roles and the marker or autosave would write the filtered roles back.
                const incomingHealthy = doc.storedDataInvalid === false;
                const keepsExistingRoles = doc.roles.length === 0 && !incomingHealthy;
                const storedDataInvalid = incomingHealthy
                    ? false
                    : Boolean(doc.storedDataInvalid || (keepsExistingRoles && existing.storedDataInvalid));
                const incoming = { ...doc };
                delete incoming.storedDataInvalid;
                return {
                    ...incoming,
                    ...(storedDataInvalid ? { storedDataInvalid: true } : {}),
                    pdfDataUrl: existing.pdfDataUrl || doc.pdfDataUrl,
                    roles: keepsExistingRoles ? existing.roles : doc.roles,
                    description: doc.description || existing.description,
                    language: doc.language || existing.language,
                    delayedSigning: doc.delayedSigning ?? existing.delayedSigning,
                    sourceLabel: doc.sourceLabel || existing.sourceLabel,
                    sourceVersion: doc.sourceVersion || existing.sourceVersion,
                    sourceVersionDate: doc.sourceVersionDate || existing.sourceVersionDate,
                    sourceFileName: doc.sourceFileName || existing.sourceFileName,
                    elements: doc.elements.length > 0 ? doc.elements : existing.elements,
                    carryForward: doc.carryForward ?? existing.carryForward,
                    configurationDataAudit: (doc.configurationDataAudit?.length ?? 0) > 0
                        ? doc.configurationDataAudit
                        : existing.configurationDataAudit,
                };
            });
            const localOnly = state.documents.filter((doc) => !incomingIds.has(doc.id));
            return { ...state, documents: [...merged, ...localOnly] };
        }
        case 'ADD_DOCUMENT':
            return { ...state, documents: [...state.documents, action.payload] };
        case 'UPDATE_DOCUMENT':
            return {
                ...state,
                documents: state.documents.map((doc) => doc.id === action.payload.id
                    ? {
                        ...action.payload,
                        // Library GET never includes PDF bytes. hydrateDocument uses this action, and
                        // wiping a prefetched data URL leaves the canvas on react-pdf's empty-file error.
                        pdfDataUrl: action.payload.pdfDataUrl || doc.pdfDataUrl,
                    }
                    : doc),
            };
        case 'ARCHIVE_DOCUMENT':
            return {
                ...state,
                documents: state.documents.map((doc) => doc.id === action.payload ? { ...doc, status: 'Archived', updatedAt: new Date().toISOString() } : doc),
            };
        case 'DELETE_DOCUMENT':
            return {
                ...state,
                documents: state.documents.filter((doc) => doc.id !== action.payload),
                activeDocumentId: state.activeDocumentId === action.payload ? null : state.activeDocumentId,
            };
        case 'SET_ACTIVE_DOCUMENT':
            return { ...state, activeDocumentId: action.payload, selectedElementId: null };
        case 'SET_SELECTED_ELEMENT':
            return { ...state, selectedElementId: action.payload };
        case 'SET_DOCUMENT_PDF': {
            const { documentId, pdfDataUrl } = action.payload;
            return {
                ...state,
                documents: state.documents.map((doc) => (doc.id === documentId ? { ...doc, pdfDataUrl } : doc)),
            };
        }
        case 'ADD_ELEMENT': {
            const { documentId, element } = action.payload;
            return {
                ...state,
                documents: state.documents.map((doc) => doc.id === documentId
                    ? { ...doc, elements: [...doc.elements, element], updatedAt: new Date().toISOString() }
                    : doc),
            };
        }
        case 'AUTO_MAP_ELEMENTS': {
            const { documentId, elements } = action.payload;
            if (elements.length === 0) {
                return state;
            }
            return {
                ...state,
                documents: state.documents.map((doc) => doc.id === documentId
                    ? { ...doc, elements: [...doc.elements, ...elements], updatedAt: new Date().toISOString() }
                    : doc),
            };
        }
        case 'UPDATE_ELEMENT': {
            const { documentId, element } = action.payload;
            return {
                ...state,
                documents: state.documents.map((doc) => doc.id === documentId
                    ? {
                        ...doc,
                        elements: doc.elements.map((el) => (el.id === element.id ? element : el)),
                        updatedAt: new Date().toISOString(),
                    }
                    : doc),
            };
        }
        case 'DELETE_ELEMENT': {
            const { documentId, elementId } = action.payload;
            return {
                ...state,
                selectedElementId: state.selectedElementId === elementId ? null : state.selectedElementId,
                documents: state.documents.map((doc) => doc.id === documentId
                    ? {
                        ...doc,
                        elements: doc.elements.filter((el) => el.id !== elementId),
                        updatedAt: new Date().toISOString(),
                    }
                    : doc),
            };
        }
        case 'UPDATE_DOCUMENT_ROLES': {
            const { documentId, roles } = action.payload;
            return {
                ...state,
                documents: state.documents.map((doc) => (doc.id === documentId ? { ...doc, roles } : doc)),
            };
        }
        case 'UPDATE_DOCUMENT_LANGUAGE': {
            const { documentId, language } = action.payload;
            return {
                ...state,
                documents: state.documents.map((doc) => doc.id === documentId ? { ...doc, language, updatedAt: new Date().toISOString() } : doc),
            };
        }
        case 'UPDATE_DOCUMENT_DELAYED_SIGNING': {
            const { documentId, delayedSigning } = action.payload;
            return {
                ...state,
                documents: state.documents.map((doc) => doc.id === documentId ? { ...doc, delayedSigning, updatedAt: new Date().toISOString() } : doc),
            };
        }
        case 'UPDATE_DOCUMENT_NAME': {
            const { documentId, name } = action.payload;
            return {
                ...state,
                documents: state.documents.map((doc) => doc.id === documentId ? { ...doc, name, updatedAt: new Date().toISOString() } : doc),
            };
        }
        case 'UPDATE_DOCUMENT_DESCRIPTION': {
            const { documentId, description } = action.payload;
            return {
                ...state,
                documents: state.documents.map((doc) => doc.id === documentId ? { ...doc, description, updatedAt: new Date().toISOString() } : doc),
            };
        }
        case 'UPDATE_DOCUMENT_SCREENS': {
            const { documentId, instructionScreen, completionScreen } = action.payload;
            return {
                ...state,
                documents: state.documents.map((doc) => doc.id === documentId
                    ? {
                        ...doc,
                        // Each screen is applied only when this action carries it, so
                        // editing one cannot blank the other.
                        ...(instructionScreen !== undefined ? { instructionScreen } : {}),
                        ...(completionScreen !== undefined ? { completionScreen } : {}),
                        updatedAt: new Date().toISOString(),
                    }
                    : doc),
            };
        }
        case 'UPDATE_DOCUMENT_CUSTOM_FIELDS': {
            const { documentId, customFields } = action.payload;
            return {
                ...state,
                documents: state.documents.map((doc) => doc.id === documentId ? { ...doc, customFields, updatedAt: new Date().toISOString() } : doc),
            };
        }
        case 'UPDATE_DOCUMENT_SOURCE': {
            const { documentId, ...source } = action.payload;
            return {
                ...state,
                documents: state.documents.map((doc) => doc.id === documentId ? { ...doc, ...source, updatedAt: new Date().toISOString() } : doc),
            };
        }
        case 'MERGE_DOCUMENT': {
            const { documentId, changes } = action.payload;
            return {
                ...state,
                documents: state.documents.map((doc) => {
                    if (doc.id !== documentId) {
                        return doc;
                    }
                    const confirmedNow = Boolean(changes.carryForward?.placementsConfirmed) && !doc.carryForward?.placementsConfirmed;
                    const next = { ...doc, ...changes };
                    if (confirmedNow) {
                        next.elements = doc.elements.map((element) => element.needsDimensionConfirmation ? { ...element, needsDimensionConfirmation: false } : element);
                    }
                    return next;
                }),
            };
        }
        default:
            return state;
    }
};
